import { axios } from './axios.js';

/**
 * Get Google OAuth authorization URL
 * @param {Object} options - Options
 * @param {boolean} options.popup - Whether to open in popup mode
 */
export async function getGoogleAuthUrl(options = {}) {
  try {
    const params = options.popup ? { popup: 'true' } : {};
    const response = await axios.get('/integrations/google/auth-url', {
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to get Google auth URL:', error);
    throw error;
  }
}

/**
 * Open Google OAuth in a popup window
 * @returns {Promise<{success: boolean, email?: string, error?: string}>}
 */
export function openGoogleOAuthPopup() {
  return new Promise(async (resolve, reject) => {
    try {
      // Get the auth URL with popup mode
      const { authUrl } = await getGoogleAuthUrl({ popup: true });

      // Calculate popup position (center of screen)
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      // Open popup
      const popup = window.open(
        authUrl,
        'google-oauth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`,
      );

      if (!popup) {
        reject(new Error('Popup blocked. Please allow popups for this site.'));
        return;
      }

      // Listen for message from popup
      const messageHandler = event => {
        // Verify the message is from our popup
        if (event.data && event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
          window.removeEventListener('message', messageHandler);
          resolve({ success: true, email: event.data.email });
        } else if (event.data && event.data.type === 'GOOGLE_OAUTH_ERROR') {
          window.removeEventListener('message', messageHandler);
          resolve({ success: false, error: event.data.error });
        }
      };

      window.addEventListener('message', messageHandler);

      // Also check if popup is closed without completing OAuth
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          // Give a small delay to allow any pending messages
          setTimeout(() => {
            resolve({
              success: false,
              error: 'Popup closed without completing authentication',
            });
          }, 500);
        }
      }, 500);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Disconnect Google account
 */
export async function disconnectGoogleAccount() {
  try {
    const response = await axios.post('/integrations/google/disconnect');
    return response.data;
  } catch (error) {
    console.error('Failed to disconnect Google account:', error);
    throw error;
  }
}

/**
 * Get Google account connection status
 */
export async function getGoogleStatus() {
  try {
    const response = await axios.get('/integrations/google/status');
    return response.data;
  } catch (error) {
    console.error('Failed to get Google status:', error);
    throw error;
  }
}

/**
 * Send email via Gmail
 */
export async function sendEmail({ to, cc, bcc, subject, body, isHtml }) {
  try {
    const response = await axios.post('/integrations/google/gmail/send', {
      to,
      cc,
      bcc,
      subject,
      body,
      isHtml,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to send email:', error);
    throw error;
  }
}

/**
 * Create calendar meeting
 */
export async function createMeeting({
  title,
  description,
  startDateTime,
  endDateTime,
  attendees,
  timeZone,
}) {
  try {
    const response = await axios.post('/integrations/google/calendar/meeting', {
      title,
      description,
      startDateTime,
      endDateTime,
      attendees,
      timeZone,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to create meeting:', error);
    throw error;
  }
}

/**
 * List calendar events
 */
export async function listCalendarEvents(maxResults = 10) {
  try {
    const response = await axios.get('/integrations/google/calendar/events', {
      params: { maxResults },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to list calendar events:', error);
    throw error;
  }
}
